import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, BadgeCheck, MessageSquare } from "lucide-react";
import axios from "axios";
import { useStore } from "../context/StoreContext";
import { PRODUCTS } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Stars = ({ n, size = 13 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n}/5`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} className={i <= n ? "fill-[#C85A32] text-[#C85A32]" : "text-stone-300"} />)}
  </span>
);

export const ReviewCard = ({ r, i, lang, verifiedLabel }) => {
  const product = PRODUCTS.find((p) => p.id === r.product_id);
  return (
    <motion.figure
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.12 }}
      className="bg-white rounded-2xl border border-stone-200 p-8 flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
      data-testid={`review-card-${r.id}`}
    >
      <Stars n={r.rating} />
      <blockquote className="mt-5 font-serif text-lg lg:text-xl italic text-stone-800 leading-relaxed flex-1">“{r.text}”</blockquote>
      <figcaption className="mt-6 pt-5 border-t border-stone-200 flex items-end justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-stone-900">{r.author}</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500 mt-1">
            {product ? product.name[lang] : r.product_id} · {new Date(r.created_at).toLocaleDateString(lang === "fr" ? "fr-CH" : "en-GB", { month: "short", year: "numeric" })}
          </p>
        </div>
        {r.verified && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide text-[#4A624E]"><BadgeCheck size={12} /> {verifiedLabel}</span>}
      </figcaption>
    </motion.figure>
  );
};

export const Testimonials = () => {
  const { t, lang } = useStore();
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  return (
    <section className="bg-[#F3EFEA] py-24 lg:py-36 px-6 lg:px-14 xl:px-24" data-testid="testimonials-section">
      <div className="max-w-[1500px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }} className="mb-16 grid lg:grid-cols-2 gap-8 lg:items-end">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#C85A32]" />
              {t.reviews.overline}
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl tracking-tight text-stone-900 max-w-lg">{t.reviews.title}</h2>
          </div>
          <p className="text-sm text-stone-600 leading-relaxed max-w-md lg:justify-self-end" data-testid="reviews-intro">{t.reviews.intro}</p>
        </motion.div>

        {reviews && reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {reviews.slice(0, 6).map((r, i) => <ReviewCard key={r.id} r={r} i={i} lang={lang} verifiedLabel={t.reviews.verified} />)}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-stone-300 p-12 text-center" data-testid="reviews-empty">
            <MessageSquare size={28} className="mx-auto text-stone-300" />
            <p className="mt-5 font-serif text-2xl italic text-stone-800">{t.reviews.empty}</p>
            <p className="mt-2 text-sm text-stone-500">{t.reviews.emptySub}</p>
          </div>
        )}
      </div>
    </section>
  );
};
